/**
 * Program 12.1 UX4 — Bid workspace view model (FE aggregation).
 */
import { buildBidDecisionReadiness } from './bidDecisionReadiness';
import {
  buildEstimateSummaryReadiness,
  buildProposalReadiness,
  buildSubmissionReadiness,
} from './estimateProposalSubmissionReadiness';
import { buildTenderIntakeReadiness } from './tenderIntakeReadiness';
import { parseBidDimensions, outcomeDisplayLabel, computeOverallScore } from './bidDecisionDimensions';
import { buildBidBondReadiness } from './bidBondTracker';

export type BidAttentionItem = {
  id: string;
  severity: 'info' | 'warning' | 'critical';
  title: string;
  detail?: string;
  href?: string;
};

export type BidStageSummary = {
  key: string;
  label: string;
  status: string;
  detail?: string;
  href?: string;
};

export type BidWorkspaceViewModel = {
  tenderId: string;
  tenderTitle: string;
  clientName: string;
  submissionDue: string | null;
  submissionDueLabel: string;
  primaryBidDecision: Record<string, unknown> | null;
  isNoBidApproved: boolean;
  bidDecisionReady: boolean;
  attention: BidAttentionItem[];
  stages: BidStageSummary[];
  overallSubmissionReadiness: number;
  submissionBlockerCount: number;
};

function asStr(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

export function formatDeadlineDisplay(raw: unknown, timezoneLabel = 'GST'): string {
  if (!raw) return '—';
  const d = new Date(String(raw));
  if (Number.isNaN(d.getTime())) return String(raw);
  const datePart = d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const timePart = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${datePart} · ${timePart} ${timezoneLabel}`;
}

export function buildBidWorkspaceViewModel(input: {
  tender: Record<string, unknown>;
  bidDecisions: Record<string, unknown>[];
  proposals: Record<string, unknown>[];
  submissions: Record<string, unknown>[];
  clarifications: Record<string, unknown>[];
  addenda: Record<string, unknown>[];
  estimateCount?: number;
  approvedRevisionCount?: number;
  latestRevisionStatus?: string | null;
}): BidWorkspaceViewModel {
  const tenderId = String(input.tender.id || '');
  const primaryBd = input.bidDecisions[0] || null;
  const primaryProposal = input.proposals[0] || null;
  const primarySubmission = input.submissions[0] || null;

  const hasApprovedGo = input.bidDecisions.some((b) => {
    const outcome = asStr(b.outcome).toUpperCase();
    const status = asStr(b.status).toLowerCase();
    return status === 'approved' && (outcome === 'GO' || outcome === 'CONDITIONAL');
  });

  const isNoBidApproved = input.bidDecisions.some((b) => {
    const outcome = asStr(b.outcome).toUpperCase();
    const status = asStr(b.status).toLowerCase();
    return status === 'approved' && outcome === 'NO_GO';
  });

  const bidReadiness = primaryBd ? buildBidDecisionReadiness(primaryBd) : null;
  const proposalReadiness = primaryProposal ? buildProposalReadiness(primaryProposal) : null;
  const estimateReadiness = buildEstimateSummaryReadiness({
    estimateCount: input.estimateCount ?? 0,
    approvedRevisionCount: input.approvedRevisionCount ?? 0,
    latestRevisionStatus: input.latestRevisionStatus,
  });

  const bondMeta = (input.tender.meta_json || input.tender.metaJson) as Record<string, unknown> | undefined;
  const bondReadiness = buildBidBondReadiness(bondMeta?.bid_bond);

  const openClarifications = input.clarifications.filter(
    (c) => !/closed|answered/i.test(asStr(c.status))
  ).length;
  const unincorporatedAddenda = input.addenda.filter((a) => {
    const st = asStr(a.status).toLowerCase();
    return st !== 'incorporated' && st !== 'fully_incorporated';
  }).length;

  const submissionReadiness = primarySubmission
    ? buildSubmissionReadiness({
        submission: primarySubmission,
        proposal: primaryProposal,
        hasApprovedGo,
        submissionDueAt: asStr(input.tender.submission_due_at || input.tender.submissionDueAt) || null,
        openClarificationCount: openClarifications,
        unincorporatedAddendaCount: unincorporatedAddenda,
        bidBondReady: bondReadiness.ready || bondReadiness.status === 'NOT_REQUIRED',
      })
    : null;

  const attention: BidAttentionItem[] = [];

  if (isNoBidApproved) {
    attention.push({
      id: 'no-bid',
      severity: 'info',
      title: 'NO-BID decision approved',
      detail: 'Downstream bid preparation is not required',
    });
  }

  if (primaryBd) {
    const dims = parseBidDimensions(primaryBd.factors_json || primaryBd.factorsJson);
    const score = computeOverallScore(dims);
    if (!score && asStr(primaryBd.outcome) === 'PENDING') {
      attention.push({
        id: 'bid-pending',
        severity: 'warning',
        title: 'Bid decision outcome pending',
        href: `#bid-decision`,
      });
    }
  }

  if (unincorporatedAddenda > 0) {
    attention.push({
      id: 'addenda-pending',
      severity: 'critical',
      title: `${unincorporatedAddenda} addendum(addenda) not incorporated`,
      href: `#addenda`,
    });
  }

  if (openClarifications > 0) {
    attention.push({
      id: 'clar-open',
      severity: 'warning',
      title: `${openClarifications} open clarification(s)`,
      href: `#clarifications`,
    });
  }

  if (!bondReadiness.ready && bondReadiness.status !== 'NOT_REQUIRED') {
    attention.push({
      id: 'bid-bond',
      severity: bondReadiness.blocking ? 'critical' : 'warning',
      title: `Bid bond: ${bondReadiness.status.replace(/_/g, ' ')}`,
      href: `#bid-bond`,
    });
  }

  for (const b of submissionReadiness?.blockers.filter((x) => x.severity === 'blocking') || []) {
    attention.push({
      id: `sub-block-${b.id}`,
      severity: 'critical',
      title: b.reason,
      href: `#submission`,
    });
  }

  const stages: BidStageSummary[] = [
    {
      key: 'bid-decision',
      label: 'Bid Decision',
      status: primaryBd
        ? `${outcomeDisplayLabel(asStr(primaryBd.outcome))} · ${asStr(primaryBd.status)}`
        : 'Not started',
      href: '#bid-decision',
    },
    {
      key: 'estimate',
      label: 'Estimate',
      status: estimateReadiness.overall,
      detail: input.estimateCount ? `${input.estimateCount} EST3 estimate(s)` : undefined,
      href: '#estimate',
    },
    {
      key: 'proposal',
      label: 'Proposal',
      status: primaryProposal
        ? `${asStr(primaryProposal.status)} · ${proposalReadiness?.overall || '—'}`
        : 'Not created',
      href: '#proposal',
    },
    {
      key: 'submission',
      label: 'Submission',
      status: primarySubmission
        ? `${asStr(primarySubmission.status)} · ${submissionReadiness?.overall || '—'}`
        : 'Not created',
      href: '#submission',
    },
  ];

  const checks = submissionReadiness?.checks || [];
  const readyCount = checks.filter((c) => c.state === 'READY').length;
  const overallSubmissionReadiness =
    checks.length > 0 ? Math.round((readyCount / checks.length) * 100) : 0;

  return {
    tenderId,
    tenderTitle: asStr(input.tender.title || input.tender.tender_number),
    clientName: asStr(input.tender.client_name || input.tender.clientName),
    submissionDue: asStr(input.tender.submission_due_at || input.tender.submissionDueAt) || null,
    submissionDueLabel: formatDeadlineDisplay(
      input.tender.submission_due_at || input.tender.submissionDueAt
    ),
    primaryBidDecision: primaryBd,
    isNoBidApproved,
    bidDecisionReady: bidReadiness?.canSubmit ?? false,
    attention,
    stages,
    overallSubmissionReadiness,
    submissionBlockerCount:
      submissionReadiness?.blockers.filter((b) => b.severity === 'blocking').length ?? 0,
  };
}
